import { EVENTS } from 'js-pimcore-formbuilder/src/js/constants/defaults';
import { isNode } from 'js-pimcore-formbuilder/src/js/utils/helpers';

export const OPTIONS_DEFAULTS = {
  disableFormWhileLoading: true,
  captchaFieldClass: 'input.re-captcha-v3',
};

/**
 * Implements the client-side integration of the Cloudflare Turnstile Captcha.
 */
export class TurnstileCaptcha {
  siteKey = null;

  token = null;

  captchaField = null;

  widgetId = null;

  /**
   * Initialize the Turnstile Captcha on a form element.
   *
   * @param {Element} form - The associated form element.
   * @param {Object} options - Options, see OPTIONS_DEFAULTS above.
   *
   * @constructor
   */
  constructor(form, options = {}) {
    this.form = form;
    this.options = Object.assign(OPTIONS_DEFAULTS, options);
    this.captchaField = this.form.querySelector(this.options.captchaFieldClass);
    this.initialized = false;

    if (!isNode(this.captchaField)) {
      return;
    }

    if (!window.turnstile) {
      throw new Error('Turnstile captcha script not loaded.');
    }

    this.disableFormSubmission();

    this.siteKey = this.captchaField.dataset.siteKey;
    this.action = this.captchaField.dataset.actionName;
    this.container = this.form.querySelector('.turnstile-widget');

    if (!isNode(this.container)) {
      throw new Error('Missing container for turnstile widget.');
    }

    // onReset has to be defered because the formbuilder itself will remove the 'disabled' from the submit.
    this.form.addEventListener(EVENTS.success, () => {
      setTimeout(() => this.onReset(), 0);
    });
    this.form.addEventListener(EVENTS.error, () => {
      setTimeout(() => this.onReset(), 0);
    });

    // Make sure disable interaction and reset captcha on temporary connection losses.
    window.addEventListener('offline', () => {
      this.resetToken();
    });
    window.addEventListener('online', () => {
      this.onReset();
    });

    this.initCaptcha();
  }

  /**
   *  Init the captcha.
   */
  initCaptcha() {
    window.turnstile.ready(() => {
      // The current turnstile implementation seems to send the ready event twice, only react to the first one.
      if (this.initialized) {
        return;
      }

      if (this.container) {
        this.initialized = true;
        this.injectTokenToForm();
      }
    });
  }

  /**
   * Reset form state and captcha widget.
   */
  onReset() {
    if (!this.widgetId || !this.captchaField || !window.turnstile) {
      return;
    }
    this.resetToken();
    window.turnstile.reset(this.widgetId);
  }

  /**
   * Resets the token values.
   */
  resetToken() {
    this.token = null;
    this.captchaField.value = '';
    this.disableFormSubmission();
  }

  /**
   * Start a new captcha process.
   */
  injectTokenToForm() {
    const language = (this.container.dataset?.locale || '').split('_')[0] || 'auto';

    try {
      this.widgetId = window.turnstile.render(this.container, {
        'sitekey': this.siteKey,
        'language': language,
        'action': this.action,
        'theme': 'light',
        'response-field': false,
        'callback': (token) => {
          this.token = token;
          this.captchaField.value = token;
          this.enableFormSubmission();
        },
        'error-callback': (error) => {
          // eslint-disable-next-line no-console
          console.error('Turnstile error:', error);
          this.resetToken();
        },
        'expired-callback': () => {
          this.resetToken();
        },
      });
    } catch (error) {
      this.form.dispatchEvent(new CustomEvent(EVENTS.fatalCaptcha));
      // eslint-disable-next-line no-console
      console.error('Turnstile initialization failed:', error);
    }
  }

  /**
   * Disable form submit action.
   */
  disableFormSubmission() {
    if (this.options.disableFormWhileLoading !== true) {
      return;
    }

    this.form.querySelector('[type="submit"]').setAttribute('disabled', 'disabled');
  }

  /**
   * Reenable form submit action.
   */
  enableFormSubmission() {
    if (this.options.disableFormWhileLoading !== true) {
      return;
    }

    this.form.querySelector('[type="submit"]').removeAttribute('disabled');
  }
}
